import { COLOR_TOKENS } from "~/designSystem/constants/token";

import { AvatarPulsateConfig } from "./interfaces";

// Define the color mapping object
export const AVATAR_COLOR_MAP = {
  "core-silver": {
    stroke: COLOR_TOKENS.inverted[4],
    activeStroke: COLOR_TOKENS.core.silver.a12,
    disabledStroke: COLOR_TOKENS.inverted[6],
    activeOuterShadow: COLOR_TOKENS.core.silver.a5,
    activeOuterShadow50: COLOR_TOKENS.core.silver.a3,
    activeOuterShadow100: COLOR_TOKENS.core.silver.a1,
  },
  "core-blue": {
    stroke: COLOR_TOKENS.core.blue.a4,
    activeStroke: COLOR_TOKENS.core.blue.a11,
    disabledStroke: COLOR_TOKENS.core.blue.a6,
    activeOuterShadow: COLOR_TOKENS.core.blue.a5,
    activeOuterShadow50: COLOR_TOKENS.core.blue.a3,
    activeOuterShadow100: COLOR_TOKENS.core.blue.a1,
  },
  succeeded: {
    stroke: COLOR_TOKENS.status.success.a4,
    activeStroke: COLOR_TOKENS.status.success[11],
    disabledStroke: COLOR_TOKENS.status.success.a6,
    activeOuterShadow: COLOR_TOKENS.status.success.a5,
    activeOuterShadow50: COLOR_TOKENS.status.success.a3,
    activeOuterShadow100: COLOR_TOKENS.status.success.a1,
  },
  warning: {
    stroke: COLOR_TOKENS.status.warning.a4,
    activeStroke: COLOR_TOKENS.status.warning[11],
    disabledStroke: COLOR_TOKENS.status.warning.a6,
    activeOuterShadow: COLOR_TOKENS.status.warning.a5,
    activeOuterShadow50: COLOR_TOKENS.status.warning.a3,
    activeOuterShadow100: COLOR_TOKENS.status.warning.a1,
  },
  error: {
    stroke: COLOR_TOKENS.status.error.a4,
    activeStroke: COLOR_TOKENS.status.error[11],
    disabledStroke: COLOR_TOKENS.status.error.a6,
    activeOuterShadow: COLOR_TOKENS.status.error.a5,
    activeOuterShadow50: COLOR_TOKENS.status.error.a3,
    activeOuterShadow100: COLOR_TOKENS.status.error.a1,
  },
  feature: {
    stroke: COLOR_TOKENS.status.feature.a4,
    activeStroke: COLOR_TOKENS.status.feature[11],
    disabledStroke: COLOR_TOKENS.status.feature.a6,
    activeOuterShadow: COLOR_TOKENS.status.feature.a5,
    activeOuterShadow50: COLOR_TOKENS.status.feature.a3,
    activeOuterShadow100: COLOR_TOKENS.status.feature.a1,
  },
  info: {
    stroke: COLOR_TOKENS.status.info.a4,
    activeStroke: COLOR_TOKENS.status.info[11],
    disabledStroke: COLOR_TOKENS.status.info.a6,
    activeOuterShadow: COLOR_TOKENS.status.info.a5,
    activeOuterShadow50: COLOR_TOKENS.status.info.a3,
    activeOuterShadow100: COLOR_TOKENS.status.info.a1,
  },
  pink: {
    stroke: COLOR_TOKENS.pink.a4,
    activeStroke: COLOR_TOKENS.pink[11],
    disabledStroke: COLOR_TOKENS.pink.a6,
    activeOuterShadow: COLOR_TOKENS.pink.a5,
    activeOuterShadow50: COLOR_TOKENS.pink.a3,
    activeOuterShadow100: COLOR_TOKENS.pink.a1,
  },
  plum: {
    stroke: COLOR_TOKENS.plum.a4,
    activeStroke: COLOR_TOKENS.plum[11],
    disabledStroke: COLOR_TOKENS.plum.a6,
    activeOuterShadow: COLOR_TOKENS.plum.a5,
    activeOuterShadow50: COLOR_TOKENS.plum.a3,
    activeOuterShadow100: COLOR_TOKENS.plum.a1,
  },
  lime: {
    stroke: COLOR_TOKENS.lime.a4,
    activeStroke: COLOR_TOKENS.lime[11],
    disabledStroke: COLOR_TOKENS.lime.a6,
    activeOuterShadow: COLOR_TOKENS.lime.a5,
    activeOuterShadow50: COLOR_TOKENS.lime.a3,
    activeOuterShadow100: COLOR_TOKENS.lime.a1,
  },
  jade: {
    stroke: COLOR_TOKENS.jade.a4,
    activeStroke: COLOR_TOKENS.jade[11],
    disabledStroke: COLOR_TOKENS.jade.a6,
    activeOuterShadow: COLOR_TOKENS.jade.a5,
    activeOuterShadow50: COLOR_TOKENS.jade.a3,
    activeOuterShadow100: COLOR_TOKENS.jade.a1,
  },
  amber: {
    stroke: COLOR_TOKENS.amber.a4,
    activeStroke: COLOR_TOKENS.amber[11],
    disabledStroke: COLOR_TOKENS.amber.a6,
    activeOuterShadow: COLOR_TOKENS.amber.a5,
    activeOuterShadow50: COLOR_TOKENS.amber.a3,
    activeOuterShadow100: COLOR_TOKENS.amber.a1,
  },
  brown: {
    stroke: COLOR_TOKENS.brown.a4,
    activeStroke: COLOR_TOKENS.brown[11],
    disabledStroke: COLOR_TOKENS.brown.a6,
    activeOuterShadow: COLOR_TOKENS.brown.a5,
    activeOuterShadow50: COLOR_TOKENS.brown.a3,
    activeOuterShadow100: COLOR_TOKENS.brown.a1,
  },
  indigo: {
    stroke: COLOR_TOKENS.indigo.a4,
    activeStroke: COLOR_TOKENS.indigo[11],
    disabledStroke: COLOR_TOKENS.indigo.a6,
    activeOuterShadow: COLOR_TOKENS.indigo.a5,
    activeOuterShadow50: COLOR_TOKENS.indigo.a3,
    activeOuterShadow100: COLOR_TOKENS.indigo.a1,
  },
};

export const AVATAR_OVERLAY = {
  hover: COLOR_TOKENS.base[5],
  disabled: COLOR_TOKENS.base[9],
  active: COLOR_TOKENS.base[1],
};

export const AVATAR_OPACITY = {
  hover: 0.5,
  disabled: 0.2,
  active: 0.7,
};

export const AVATAR_STYLED_PROPS = {
  LOGO: ["$size", "$status", "$stroke", "$hoverable"],
  IMAGE: ["$size", "$status", "$stroke", "$color", "$hoverable"],
  ICON: ["$size", "$status", "$stroke", "$hoverable"],
  FALLBACK: [
    "$size",
    "$status",
    "$stroke",
    "$color",
    "$hoverable",
    "$isGrayInitial",
  ],
} as const;

export const AVATAR_PULSATE_DEFAULT_CONFIG: AvatarPulsateConfig = {
  baseVariable: "--fallback-avatar-active-outer-shadow",
  sizes: {
    initial: "0 0 0 0",
    step1: "0 0 0 2px",
    step2: "0 0 0 6px",
    step3: "0 0 8px 8px",
    final: "0 0 0 0",
  },
  shadows: {
    base: "-base",
    medium: "-medium",
    light: "-light",
  },
};
